import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Edit,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { updateItem } from '../../store/slices/inventorySlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { InventoryItem } from '../../types/models';

const EditItemDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { groups } = useSelector((state: RootState) => state.inventory);
  
  const [itemName, setItemName] = useState('');
  const [itemGroup, setItemGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const item = dialog.data as InventoryItem;
  const isOpen = dialog.open && dialog.type === 'edit-item';

  useEffect(() => {
    if (item && isOpen) {
      setItemName(item.itemName);
      setItemGroup(item.itemGroup || null);
      setError(null);
    }
  }, [item, isOpen]);

  const handleClose = () => {
    dispatch(closeDialog());
    setItemName('');
    setItemGroup(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!itemName.trim()) {
      setError('Item name is required');
      return;
    }

    if (!item?.id) {
      setError('Invalid item');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updates: Partial<InventoryItem> = {
        itemName: itemName.trim(),
        itemGroup: itemGroup || undefined,
        lastUpdated: Date.now() / 1000,
      };

      await FirestoreService.updateInventoryItem(item.id, updates);

      // Update local state immediately for optimistic UI
      const updatedItem: InventoryItem = {
        ...item,
        ...updates,
      };
      dispatch(updateItem(updatedItem));

      dispatch(addNotification({
        message: `${itemName} updated successfully`,
        type: 'success',
      }));

      handleClose();
    } catch (error: any) {
      console.error('Error updating item:', error);
      setError(error.message || 'Failed to update item');
      dispatch(addNotification({
        message: `Failed to update ${itemName}`,
        type: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = item && (
    itemName.trim() !== item.itemName || 
    (itemGroup || undefined) !== item.itemGroup
  );

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Edit color="primary" />
          <Box>
            <Typography variant="h6">Edit Item</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {item?.itemName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="normal"
            label="Item Name"
            fullWidth
            required
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="Enter item name"
            disabled={loading}
            helperText="Give your item a clear, descriptive name"
          />

          <Autocomplete
            value={itemGroup}
            onChange={(event, newValue) => setItemGroup(newValue)}
            inputValue={itemGroup || ''}
            onInputChange={(event, newInputValue) => setItemGroup(newInputValue)}
            options={groups}
            freeSolo
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Group (Optional)"
                placeholder="Enter or select a group"
                disabled={loading}
                helperText="Group similar items together for better organization"
              />
            )}
          />

          {item && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Item Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Item ID: {item.itemID}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {item.checkedOut ? 'Checked Out' : 'Available'}
              </Typography>
              {item.itemQRFileName && (
                <Typography variant="body2" color="text.secondary">
                  QR Code: {item.itemQRFileName}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !itemName.trim() || !hasChanges}
          startIcon={loading ? <CircularProgress size={20} /> : <Edit />}
        >
          {loading ? 'Updating...' : 'Update Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemDialog;